import React from 'react';

/**
 * Global error boundary.
 *
 * Catches render-time errors anywhere below it in the tree and shows a
 * friendly fallback UI instead of a blank white screen.
 *
 * Limitations (by design - this is React's contract, not a bug):
 *  - Does NOT catch errors in event handlers (use try/catch there).
 *  - Does NOT catch errors in async code (promises, setTimeout, fetch
 *    callbacks). Those bubble up to window.onunhandledrejection.
 *  - Does NOT catch errors during server-side rendering.
 *
 * Hooks for observability:
 *  - componentDidCatch logs to console today; once Sentry is wired in,
 *    we will forward `error` and `errorInfo.componentStack` there.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render error captured:', error, errorInfo);
    // TODO(sentry): forward to Sentry once REACT_APP_SENTRY_DSN is configured.
    if (typeof window !== 'undefined' && window.Sentry && typeof window.Sentry.captureException === 'function') {
      try {
        window.Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
      } catch (_) { /* never let the boundary itself throw */ }
    }
  }

  handleReload = () => {
    // Hard reload clears any in-memory bad state. We also bust the SW cache
    // by appending a query string so the next request can fetch fresh assets
    // even if the service worker is serving a stale shell.
    const sep = window.location.search ? '&' : '?';
    window.location.href = `${window.location.pathname}${window.location.search}${sep}_eb=${Date.now()}`;
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          textAlign: 'center',
          color: '#222',
          background: '#fff',
        }}
      >
        <h1 style={{ marginBottom: '0.5rem', fontSize: '1.4rem' }}>
          Algo salió mal en la aplicación
        </h1>
        <p style={{ marginBottom: '1.5rem', maxWidth: 480, lineHeight: 1.5 }}>
          Tu sesión sigue activa. Recargá la página para volver a intentarlo.
          Si el problema persiste, contactá al administrador del gimnasio.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: '0.6rem 1.4rem',
            fontSize: '1rem',
            border: 'none',
            borderRadius: 6,
            background: '#111',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          Recargar
        </button>
        {process.env.NODE_ENV !== 'production' && this.state.error && (
          <pre
            style={{
              marginTop: '1.5rem',
              padding: '1rem',
              maxWidth: '90vw',
              overflow: 'auto',
              background: '#f6f6f6',
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: '0.8rem',
              textAlign: 'left',
            }}
          >
            {String(this.state.error.stack || this.state.error)}
          </pre>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
